// ═══════════════════════════════════════════════════════════════
// FISCAL YEAR UTILITIES
// Path: src/utils/fiscalYear.js
//
// Computes fiscal year date ranges based on a start month.
// Start month = 1 (January) means calendar year.
// Start month = 7 (July) means fiscal year runs Jul 1 - Jun 30.
// ═══════════════════════════════════════════════════════════════

/**
 * Get the current fiscal year start and end dates.
 * @param {number} startMonth — 1-12 (1 = January, 7 = July, etc.)
 * @returns {{ yearStart: string, yearEnd: string, quarterStart: string, monthStart: string, label: string }}
 */
export function getFiscalDates(startMonth = 1) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based

  // ── Fiscal Year ─────────────────────────────────────────
  let fyStartYear;
  if (currentMonth >= startMonth) {
    fyStartYear = currentYear;
  } else {
    fyStartYear = currentYear - 1;
  }

  const yearStart = `${fyStartYear}-${String(startMonth).padStart(2, "0")}-01`;

  // End is one year later minus one day
  let fyEndYear = fyStartYear + 1;
  let fyEndMonth = startMonth - 1;
  if (fyEndMonth === 0) {
    fyEndMonth = 12;
    fyEndYear = fyStartYear;
  }
  // Last day of the month before start month next year
  const yearEnd = lastDayOfMonth(fyEndYear, fyEndMonth);

  // ── Fiscal Quarter ──────────────────────────────────────
  // Quarters are 3-month blocks from the fiscal year start
  const monthsIntoFY = currentMonth >= startMonth
    ? currentMonth - startMonth
    : currentMonth + 12 - startMonth;
  const quarterIndex = Math.floor(monthsIntoFY / 3); // 0, 1, 2, 3
  const quarterStartMonth = ((startMonth - 1 + quarterIndex * 3) % 12) + 1;
  const quarterStartYear = startMonth + quarterIndex * 3 > 12
    ? fyStartYear + 1
    : fyStartYear;
  // Correct for year wraparound
  const qsYear = quarterStartMonth < startMonth ? fyStartYear + 1 : fyStartYear;
  const quarterStart = `${qsYear}-${String(quarterStartMonth).padStart(2, "0")}-01`;

  // ── Current Month ───────────────────────────────────────
  const monthStart = `${currentYear}-${String(currentMonth).padStart(2, "0")}-01`;

  // ── Label ───────────────────────────────────────────────
  const label = startMonth === 1
    ? `CY ${currentYear}`
    : `FY ${fyStartYear}/${String(fyStartYear + 1).slice(2)}`;

  return {
    yearStart,
    yearEnd,
    quarterStart,
    monthStart,
    label,
    startMonth,
  };
}

function lastDayOfMonth(year, month) {
  // Day 0 of next month = last day of this month
  const d = new Date(year, month, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ═══════════════════════════════════════════════════════════════
// Current + Last FY boundaries — used by the Pollen donor-state
// classification (Second Gift / Renewals / LYBNT / SYBNT).
//
// Returns YYYY-MM-DD strings that sort and compare correctly
// against the gift_date DATE column in Supabase.
// ═══════════════════════════════════════════════════════════════

const clampMonth = (m) => {
  const n = parseInt(m, 10);
  if (Number.isNaN(n)) return 1;
  return Math.min(Math.max(n, 1), 12);
};

/**
 * @param {number} startMonth — 1-12
 * @param {Date} [now] — injectable for testing
 * @returns {{ currentFyStart: string, lastFyStart: string, lastFyEnd: string }}
 */
export const getFiscalYearBoundaries = (startMonth = 1, now = new Date()) => {
  const safeStart = clampMonth(startMonth);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based

  const currentFyStartYear =
    currentMonth >= safeStart ? currentYear : currentYear - 1;
  const mm = String(safeStart).padStart(2, "0");
  const currentFyStart = `${currentFyStartYear}-${mm}-01`;

  const lastFyStartYear = currentFyStartYear - 1;
  const lastFyStart = `${lastFyStartYear}-${mm}-01`;

  const endMonth = safeStart === 1 ? 12 : safeStart - 1;
  const endYear = safeStart === 1 ? lastFyStartYear : currentFyStartYear;
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  const lastFyEnd = `${endYear}-${String(endMonth).padStart(2, "0")}-${String(
    lastDay
  ).padStart(2, "0")}`;

  return { currentFyStart, lastFyStart, lastFyEnd };
};

/**
 * Classify a gift_date string against FY boundaries.
 * @returns {"current" | "last" | "older" | null}
 */
export const classifyGiftDate = (giftDate, boundaries) => {
  if (!giftDate) return null;
  const { currentFyStart, lastFyStart } = boundaries;
  if (giftDate >= currentFyStart) return "current";
  if (giftDate >= lastFyStart) return "last";
  return "older";
};

/**
 * Reduce a member's gift_date list to the three FY buckets.
 * @returns {{ gaveCurrentFy: boolean, gaveLastFy: boolean, gaveOlder: boolean }}
 */
export const summarizeGiftsByFy = (giftDates, boundaries) => {
  let gaveCurrentFy = false;
  let gaveLastFy = false;
  let gaveOlder = false;
  for (const date of giftDates) {
    const bucket = classifyGiftDate(date, boundaries);
    if (bucket === "current") gaveCurrentFy = true;
    else if (bucket === "last") gaveLastFy = true;
    else if (bucket === "older") gaveOlder = true;
    if (gaveCurrentFy && gaveLastFy && gaveOlder) break;
  }
  return { gaveCurrentFy, gaveLastFy, gaveOlder };
};

/**
 * Month options for the fiscal year selector in Settings.
 */
export const FISCAL_MONTH_OPTIONS = [
  { value: 1, label: "January (Calendar Year)" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];
